import { useCallback, useEffect, useRef } from 'react';
import { mergeCatalogEvents } from '../utils/catalogDelta';

// One in-flight read, with the union of events arriving during it queued for
// the next read. Every caller can await the reconciliation it requested.
const useCatalogUpdateQueue = (scope, refresh) => {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const ownerRef = useRef(null);
  if (ownerRef.current?.scope !== scope) ownerRef.current = { scope, active: true, pending: null, waiters: [], timer: null, running: false };
  const owner = ownerRef.current;
  useEffect(() => {
    owner.active = true;
    return () => {
      owner.active = false;
      clearTimeout(owner.timer);
      owner.waiters.splice(0).forEach(({ resolve }) => resolve(false));
    };
  }, [owner]);
  return useCallback((event = {}) => {
    const promise = new Promise((resolve, reject) => {
    const state = ownerRef.current;
    if (!state.active) { resolve(false); return; }
    state.pending = mergeCatalogEvents(state.pending, event);
    state.waiters.push({ resolve, reject });
    const drain = async () => {
      if (state.running || !state.active) return;
      state.running = true;
      while (state.pending && state.active) {
        const next = state.pending;
        state.pending = null;
        const waiters = state.waiters.splice(0);
        try {
          const result = await refreshRef.current(next);
          waiters.forEach((waiter) => waiter.resolve(result));
        } catch (error) {
          waiters.forEach((waiter) => waiter.reject(error));
        }
      }
      state.running = false;
    };
    clearTimeout(state.timer);
    state.timer = setTimeout(() => { void drain(); }, 250);
    });
    promise.catch(() => undefined);
    return promise;
  }, []);
};

export default useCatalogUpdateQueue;
